import axios from "axios";
import { message } from "antd";

import Decimal from "decimal.js";

/**
 * @description Obtiene un nuevo folio para la Nota de Crédito
 * @returns folio 
 */
export const getNewFolioNC = async () => {
    try {
        let response = await axios.get("/nota-credito/new-folio")
        return response.data
    } catch (error) {
        message.error("Error al obtener el nuevo folio de Nota de Crédito.");
        return null;
    }
}


/**
 * @description Obtiene un nuevo folio según el tipo de documento
 * @param {string} tipo - Tipo de documento (por ejemplo, "NC" para Nota de Crédito)
 * @returns {Promise<string|null>} Folio del nuevo documento o null en caso de error
 */
export const getNewFolio = async (tipo) => {
    if (tipo === "NC") {
        return getNewFolioNC();
    }
    return null;
}

/**
 * @description Obtiene una factura por su ID
 * @param {string} id - ID de la factura
 * @returns {Promise<Object|null>} Objeto de la factura o null en caso de error
 */
export const getFactura = async (id) => {
    try {

        let response = await axios.get('/facturas/get', {
            params: { 
                id,
            },
            headers: { Authorization: sessionStorage.getItem('token') }
        })
        return response?.data?.data
    
    } catch (error) {
        message.error("Error al obtener la factura. (services)");    
        return null;
    }
}


/**
 * @memberof DrawerTimbrado
 * @method getConceptosFromFacturas
 * @description Obtiene los conceptos y totales de una factura y los agrega a los conceptos existentes
 * @param {Object|null} newFactura - Nueva factura a agregar o null si se eliminó una factura
 * @param {Array} facturas - Lista actual de facturas
 * @returns {Object} Objeto con conceptos_facturas, monto e imp_trasladados
 */
export const getConceptosFromFacturas = (newFactura, facturas = []) => {
    // Si newFactura es null, solo procesamos el array de facturas existente
    let facturasList = newFactura ? [...facturas, newFactura] : facturas
    let monto = 0
    let imp_trasladados = 0
    let conceptos_facturas = []

    // Si no hay facturas, retornamos valores vacíos
    if (facturasList.length === 0) {
        return {
            conceptos_facturas: [],
            monto: 0,
            imp_trasladados: 0
        }
    }


    let facturasConceptos = facturasList.filter(f => f?.conceptos?.length > 0)
    let facturasSinConceptos = facturasList.filter(f => !f?.conceptos || f.conceptos.length === 0)

    if(facturasConceptos.length > 0) {
        //Nota: las facturas que se hacen de forma individual (SPT, VMI), si guardan conceptos (12/Mar/26)
        // Si hay conceptos, los procesamos
        for (let factura of facturasConceptos) {
            if (factura?.conceptos?.length > 0) {
                for (let cons of factura.conceptos) {

                    let tieneImpuesto = cons.Impuestos?.Traslados && cons.Impuestos.Traslados.length > 0

                    conceptos_facturas.push({ ...cons, NoImpuesto: !tieneImpuesto })
                    monto = Decimal(monto).add(parseFloat(cons.Importe)).toDecimalPlaces(2).toNumber()
                    imp_trasladados = Decimal(imp_trasladados).add(parseFloat(cons.Impuestos?.Traslados[0]?.Importe ?? 0)).toDecimalPlaces(2).toNumber()
                }
            }
        }
    }

    //En caso de que haya facturas sin conceptos, las tenemos que obtner de del objeto request
    if (facturasSinConceptos.length > 0) {
        for (let factura of facturasSinConceptos) {
            if (factura?.request?.invoice_data?.Conceptos?.length > 0) {
                for (let cons of factura.request.invoice_data.Conceptos) {

                    let importe = cons.Importe
                    let impuesto_trasladado = cons.Impuestos?.Traslados[0]?.Importe ?? 0
                    let Total = Decimal(importe).add(impuesto_trasladado).toDecimalPlaces(2).toNumber()

                    conceptos_facturas.push({ ...cons, Total, NoImpuesto: impuesto_trasladado === 0 })
                    monto = Decimal(monto).add(parseFloat(cons.Importe)).toDecimalPlaces(2).toNumber()
                    imp_trasladados = Decimal(imp_trasladados).add(parseFloat(cons.Impuestos?.Traslados[0]?.Importe ?? 0)).toDecimalPlaces(2).toNumber()
                }
            }
        }
       
    } 

    return {
        conceptos_facturas,
        monto,
        imp_trasladados,
        total: Decimal(monto).add(imp_trasladados).toDecimalPlaces(2).toNumber()
    }
}

/**
 * @description Realiza el proceso de timbrado individual para una factura 
 * @param {*} data 
 * @returns 
 */
export const timbradoIndividual = async (data) => {
    try {
        let formattedData = formatData(data)

        let response = await axios.post('/syncfy/timbrado-v2/individual', formattedData, {
            headers: { Authorization: sessionStorage.getItem('token') }
        })
        return response?.data
    } catch (error) {
        throw error;
    }
}

/**
 * @description Antes de enviar los datos al timbrado, 
 * formateamos la información para que el proceso de timbrado pueda interpretarla correctamente
 * @param {*} data 
 */
const formatData = (data) => {
    let extractFields = [
        "cliente_id",
        "razon_social_id",
        "enviar_responsable",
        "enviar_receptor",
        "facturas_ids",
    ]

    let formattedData = {}
    let invoice_data = { ...data }
    
    // Extraemos los campos que no forman parte de la estructura de la factura y los eliminamos del objeto invoice_data
    for (let field of extractFields) {
        if (data[field] !== undefined) {
            formattedData[field] = data[field]
            delete invoice_data[field]
        }
    }
    invoice_data.Version = "4.0"

    //En caso de que tener impuestos trasladados, añadimos el nodo "Impuestos.Traslados[0]"
    if (invoice_data?.Impuestos?.TotalImpuestosTrasladados) {

        //sumamos el importe de los conceptos, solo si:
        // - NoImpuesto no está marcado como true
        // - ObjetoImp no es igual a "01"
        // - El campo "Impuestos.Traslados" existe y tiene al menos un elemento
        let impuestosTrasladosBase = invoice_data.Conceptos.reduce((acc, cons) => {
            let tieneImpuesto = cons.NoImpuesto !== true && cons.ObjetoImp !== "01" && cons.Impuestos?.Traslados && cons.Impuestos.Traslados.length > 0
            if (tieneImpuesto) {
                return Decimal(acc).add(parseFloat(cons.Importe)).toDecimalPlaces(2).toNumber()
            }
            return acc
        }, 0)

        invoice_data.Impuestos.Traslados = [
            {
                Base: impuestosTrasladosBase,
                Importe: invoice_data.Impuestos.TotalImpuestosTrasladados,
                Impuesto: "002",
                TasaOCuota: "0.160000",
                TipoFactor: "Tasa"
            }
        ]
    }

    if(invoice_data?.Impuestos?.TotalImpuestosTrasladados === 0) {
        delete invoice_data.Impuestos
    }

    if(invoice_data.Moneda == "MXN") delete invoice_data.TipoCambio
    if(invoice_data.Moneda == "USD" && !invoice_data.TipoCambio) throw new Error("El tipo de cambio es requerido cuando la moneda es USD")

    formattedData.invoice_data = invoice_data

    //recorremos los conceptos para eliminar el nodo de "Impuestos" si el campo "NoImpuesto" está marcado como true,
    // u ObjetoImp es === "01" o si el campo "Impuestos.Traslados" no existe o está vacío 
    for (let i = 0; i < formattedData.invoice_data.Conceptos.length; i++) {
        let cons = formattedData.invoice_data.Conceptos[i]
        let noImpuesto = cons.NoImpuesto === true || cons.ObjetoImp === "01" || !cons.Impuestos?.Traslados || cons.Impuestos.Traslados.length === 0

        if (noImpuesto) {
            delete formattedData.invoice_data.Conceptos[i].Impuestos
        }
    }
    
    return formattedData
}

/**
 * @description Obtiene el tipo de cambio actual desde el backend, si hay un error se establece un valor por defecto de 18
 * @returns number - Tipo de cambio actual o valor por defecto
 */
export const getTipoCambioActual = async () => {
    try {
        let response = await axios.get("/tipo-cambio/get")
        return response?.data?.data[0]?.valor ?? 18
    } catch (error) {
        message.error("Error al obtener el tipo de cambio actual. Se establecerá un valor por defecto de 18.");
        return 18;
    }
}